import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';

/**
 * Change the z-order (render order) of a single board object.
 *
 * `direction === 'top'`    → rendered on top of all other objects.
 * `direction === 'bottom'` → rendered behind all other objects.
 *
 * `previousIndex` is captured before the command is pushed so that
 * `revert()` can restore the exact position.
 */
export class ZOrderCommand implements Command<BoardState> {
  readonly label: string;

  constructor(
    private readonly _objectId: string,
    private readonly _direction: 'top' | 'bottom' | 'forward' | 'backward',
    private readonly _previousIndex: number,
  ) {
    this.label = _direction === 'top'    ? 'Ganz nach oben'
      : _direction === 'bottom'          ? 'Ganz nach unten'
      : _direction === 'forward'         ? 'Eine Ebene nach oben'
      :                                    'Eine Ebene nach unten';
  }

  /** Public read-only accessor for the target object UID (PRD-009 serializer). */
  get objectId(): string { return this._objectId; }

  /** Public read-only accessor for the z-order direction (PRD-009 serializer). */
  get direction(): 'top' | 'bottom' | 'forward' | 'backward' { return this._direction; }

  /** Public read-only accessor for the index captured before apply (PRD-009 serializer). */
  get previousIndex(): number { return this._previousIndex; }

  apply(state: BoardState): void {
    if (this._direction === 'top')         state.moveToTop(this._objectId);
    else if (this._direction === 'bottom') state.moveToBottom(this._objectId);
    else if (this._direction === 'forward')
      state.moveToIndex(this._objectId, this._previousIndex + 1);
    else
      state.moveToIndex(this._objectId, Math.max(0, this._previousIndex - 1));
  }

  revert(state: BoardState): void {
    state.moveToIndex(this._objectId, this._previousIndex);
  }
}
